import { useCallback, useEffect, useRef, useState, type DependencyList } from 'react';

export interface UseAsyncDataOptions {
  debounceMs?: number;
  // Enables interval polling when set to a number. Omit (or pass undefined) for the original
  // one-shot-plus-manual-refresh behavior — existing callers are unaffected. Pages that expose a
  // manual pause/resume toggle (see PollingIndicator) implement "paused" by passing undefined
  // here rather than the hook tracking its own pause state — that way a page with several
  // useAsyncData calls (e.g. a dashboard) can pause all of them from one boolean.
  pollIntervalMs?: number;
  // Lets the caller suspend polling ticks while it's running its own mutation (retry/replay/
  // requeue/bulk-discard, a ConfirmDialog being open, etc.) so a poll response can't land in the
  // middle of — or immediately after — an in-flight action. The hook has no visibility into
  // mutations that don't go through `execute`/`refresh`, so the caller is responsible for this.
  isPollingSuspended?: boolean;
}

export function useAsyncData<T>(
  loader: () => Promise<T>,
  dependencies: DependencyList,
  options: UseAsyncDataOptions = {},
) {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestVersionRef = useRef(0);
  const isFetchingRef = useRef(false);
  const debounceMs = options.debounceMs ?? 0;
  const pollIntervalMs = options.pollIntervalMs;
  const isPollingSuspended = options.isPollingSuspended ?? false;

  const execute = useCallback(async (version: number) => {
    isFetchingRef.current = true;
    try {
      setIsLoading(true);
      setError(null);
      const result = await loader();
      if (requestVersionRef.current === version) {
        setData(result);
      }
    } catch (caught) {
      if (requestVersionRef.current === version) {
        setError(caught instanceof Error ? caught.message : 'Unexpected request failure.');
      }
    } finally {
      if (requestVersionRef.current === version) {
        setIsLoading(false);
      }
      isFetchingRef.current = false;
    }
  }, dependencies);

  const refresh = useCallback(async () => {
    const version = requestVersionRef.current + 1;
    requestVersionRef.current = version;
    await execute(version);
  }, [execute]);

  useEffect(() => {
    const version = requestVersionRef.current + 1;
    requestVersionRef.current = version;
    setIsLoading(true);

    const timeout = window.setTimeout(() => {
      void execute(version);
    }, debounceMs);

    return () => window.clearTimeout(timeout);
  }, [execute, debounceMs]);

  // Polling: fires `refresh()` on an interval, skipping any tick where firing would be wasteful
  // or risky — a fetch from this same hook is already in flight, the caller reports a mutation in
  // flight (isPollingSuspended), or the tab isn't visible. "Paused" (via the PollingIndicator UI)
  // is implemented by the caller passing `pollIntervalMs: undefined`, which tears this effect
  // down entirely.
  useEffect(() => {
    if (!pollIntervalMs) return undefined;

    const interval = window.setInterval(() => {
      if (isPollingSuspended) return;
      if (isFetchingRef.current) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      void refresh();
    }, pollIntervalMs);

    return () => window.clearInterval(interval);
  }, [pollIntervalMs, isPollingSuspended, refresh]);

  return {
    data,
    isLoading,
    error,
    refresh,
    setData,
    isPolling: Boolean(pollIntervalMs),
  };
}
